import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Countdown } from '@pacer/shared';
import * as api from '../api';
import { CountdownEditor } from './CountdownEditor';
import { Icon, type IconName } from './Icon';
import { PlanDetail } from './PlanDetail';
import { Card, IconButton, cx } from './ui';
import { daysUntil, nextBirthday, untilLabel } from '../lib/countdowns';
import { useZone } from '../lib/dates';
import { queryKeys } from '../state/queries';

interface Item {
  key: string;
  title: string;
  days: number;
  icon: IconName;
  color: string;
  /** Present for real countdowns (openable); absent for derived birthdays. */
  countdown?: Countdown;
}

/**
 * The small "Countdowns" tile: things to look forward to that aren't big enough to pin —
 * minor countdowns plus each member's next birthday (derived from members.birthday, so no
 * storage). Pinned plans are featured separately across the top of the dashboard.
 */
export function Countdowns() {
  const zone = useZone();
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState<Countdown | null>(null);

  const { data: countdowns = [] } = useQuery({ queryKey: queryKeys.countdowns, queryFn: api.getCountdowns });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    for (const c of countdowns) {
      if (c.pinned) continue; // pinned plans are shown up top
      const days = daysUntil(c.targetDate, zone);
      if (days < 0) continue;
      out.push({
        key: `cd-${c.id}`,
        title: c.title,
        days,
        icon: (c.icon as IconName) ?? 'gift',
        color: c.color ?? 'var(--color-accent)',
        countdown: c,
      });
    }
    for (const m of members) {
      if (!m.birthday) continue;
      const { days } = nextBirthday(m.birthday, zone);
      out.push({ key: `bd-${m.id}`, title: `${m.name}'s birthday`, days, icon: 'cake', color: m.color });
    }
    return out.sort((a, b) => a.days - b.days).slice(0, 6);
  }, [countdowns, members, zone]);

  const hasPinned = countdowns.some((c) => c.pinned);
  // Nothing worth a whole empty tile if there are no small items and pinned plans already show.
  if (items.length === 0 && hasPinned) {
    return (
      <>
        <AddButton onAdd={() => setEditing(true)} />
        {editing && <CountdownEditor countdown={null} onClose={() => setEditing(false)} />}
      </>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
        <Icon name="gift" size={16} className="text-muted" />
        <h2 className="font-display text-[20px] text-primary">Countdowns</h2>
        <IconButton icon="plus" label="Add countdown" size="sm" className="ml-auto" onClick={() => setEditing(true)} />
      </div>

      {items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border-subtle px-4 py-6 text-center font-mono text-[12px] text-muted">
          Add something to look forward to.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((it) => {
            const inner = (
              <Card className={cx('flex items-center gap-3 px-3 py-2.5', it.countdown && 'group cursor-pointer hover:border-border hover:bg-surface-high/60')}>
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full"
                  style={{ background: `color-mix(in srgb, ${it.color} 16%, transparent)`, color: it.color }}
                >
                  <Icon name={it.icon} size={16} />
                </span>
                <span className="min-w-0 flex-1 truncate text-[14px] text-primary">{it.title}</span>
                {it.countdown && it.countdown.tasksTotal > 0 && (
                  <span className="tabular font-mono text-[12px] text-muted">
                    {it.countdown.tasksDone}/{it.countdown.tasksTotal}
                  </span>
                )}
                <span className={cx('tabular shrink-0 font-mono text-[13px]', it.days === 0 ? 'text-accent' : 'text-secondary')}>
                  {untilLabel(it.days)}
                </span>
              </Card>
            );
            return it.countdown ? (
              <button key={it.key} onClick={() => setOpen(it.countdown!)} className="text-left">
                {inner}
              </button>
            ) : (
              <div key={it.key}>{inner}</div>
            );
          })}
        </div>
      )}

      {editing && <CountdownEditor countdown={null} onClose={() => setEditing(false)} />}
      {open && <PlanDetail countdown={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

function AddButton({ onAdd }: { onAdd: () => void }) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
        <Icon name="gift" size={16} className="text-muted" />
        <h2 className="font-display text-[20px] text-primary">Countdowns</h2>
        <IconButton icon="plus" label="Add countdown" size="sm" className="ml-auto" onClick={onAdd} />
      </div>
      <p className="rounded-lg border border-dashed border-border-subtle px-4 py-6 text-center font-mono text-[12px] text-muted">
        Add something to look forward to.
      </p>
    </section>
  );
}
